/**
 * Lead capture: attribution tracking, form validation, submission and
 * GTM dataLayer events for the Meet & Greet lead forms (inline + WhatsApp).
 *
 * No external dependencies. Loaded site-wide with `defer`.
 */
(function () {
  'use strict';

  var ATTR_STORAGE_KEY = 'ma_attr';
  var ATTR_MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000; // 90 days
  var GCLID_KEYS = ['gclid', 'gbraid', 'wbraid'];
  var TRACKED_KEYS = [
    'gclid',
    'gbraid',
    'wbraid',
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    'fbclid'
  ];
  var MIN_SUBMIT_MS = 2500;

  var config = readConfig();
  var leadFormStartFired = false;
  var formInstances = [];

  if (!config.whatsappNumber) {
    console.warn(
      'lead-form: settings.whatsapp_number is blank; the WhatsApp button and WhatsApp-mode redirects are disabled.'
    );
  }

  captureAttribution();
  onReady(initForms);
  initWhatsAppClickTracking();

  document.addEventListener('shopify:section:load', function (event) {
    var container = event.target;
    if (!container || typeof container.querySelectorAll !== 'function') return;

    var roots = [];
    if (container.matches && container.matches('[data-lead-form]')) {
      roots.push(container);
    }
    container.querySelectorAll('[data-lead-form]').forEach(function (el) {
      roots.push(el);
    });
    roots.forEach(initForm);
  });

  window.addEventListener('pageshow', function (event) {
    if (!event.persisted) return;

    formInstances.forEach(function (instance) {
      instance.reset();
    });

    var dialog = document.getElementById('WhatsappGateDialog');
    if (dialog) {
      if (typeof dialog.close === 'function' && dialog.open) {
        dialog.close();
      } else {
        dialog.classList.remove('is-open');
        dialog.removeAttribute('open');
      }
    }
  });

  /* ------------------------------------------------------------------ */
  /* Config                                                              */
  /* ------------------------------------------------------------------ */

  function readConfig() {
    var fallback = { endpoint: '', whatsappNumber: '', leadValue: 100, partyLeadValue: 150 };
    try {
      var el = document.getElementById('ma-lead-config');
      if (!el) return fallback;
      var parsed = JSON.parse(el.textContent || '{}');
      var leadValue =
        typeof parsed.leadValue === 'number' && isFinite(parsed.leadValue) ? parsed.leadValue : 100;
      var partyLeadValue =
        typeof parsed.partyLeadValue === 'number' && isFinite(parsed.partyLeadValue) ? parsed.partyLeadValue : 150;
      return {
        endpoint: parsed.endpoint || '',
        whatsappNumber: parsed.whatsappNumber || '',
        leadValue: leadValue,
        partyLeadValue: partyLeadValue
      };
    } catch (err) {
      return fallback;
    }
  }

  function onReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  /* ------------------------------------------------------------------ */
  /* Attribution capture (site-wide, first-touch)                       */
  /* ------------------------------------------------------------------ */

  function captureAttribution() { /* Managed by the Shopify privacy bridge. */ }
  function readAttribution() {
    return window.GRConsent ? window.GRConsent.attribution() : {};
  }

  /* ------------------------------------------------------------------ */
  /* Form initialisation                                                */
  /* ------------------------------------------------------------------ */

  function initForms() {
    document.querySelectorAll('[data-lead-form]').forEach(function (root) {
      initForm(root);
    });

    document.addEventListener(
      'focusin',
      function (event) {
        if (leadFormStartFired) return;
        var formEl = event.target.closest('[data-lead-form-el]');
        if (!formEl) return;
        leadFormStartFired = true;
        var root = formEl.closest('[data-lead-form]');
        var mode = root ? root.getAttribute('data-mode') : 'form';
        var formType = formEl.getAttribute('data-form-type') === 'party' ? 'party' : 'trial';
        pushDataLayer({
          event: 'lead_form_start',
          lead_source: mode === 'whatsapp' ? 'whatsapp' : 'form',
          lead_type: formType
        });
      },
      true
    );
  }

  function initForm(root) {
    if (root.hasAttribute('data-lead-form-initialized')) return;
    root.setAttribute('data-lead-form-initialized', 'true');

    var mode = root.getAttribute('data-mode') === 'whatsapp' ? 'whatsapp' : 'form';
    var form = root.querySelector('[data-lead-form-el]');
    if (!form) return;
    var formType = form.getAttribute('data-form-type') === 'party' ? 'party' : 'trial';

    var renderedAt = Date.now();
    var submitting = false;

    form.setAttribute('novalidate', 'novalidate');
    setSubmitDisabled(form, false);

    formInstances.push({
      reset: function () {
        submitting = false;
        renderedAt = Date.now();
        setSubmitDisabled(form, false);
      }
    });

    form.addEventListener('submit', async function (event) {
      event.preventDefault();
      if (submitting) return;

      var fields = getFields(form, formType);
      var errors = validate(fields, formType);

      clearErrors(form);

      if (Object.keys(errors).length) {
        showErrors(form, errors);
        return;
      }

      // Send the normalised integer (not the raw input string) in the
      // payload, the email, and the WhatsApp text - validate() above
      // already confirmed these are clean integers in range.
      fields.child_age = toInteger(fields.child_age);
      if (formType === 'party') {
        fields.guests = toInteger(fields.guests);
      }

      submitting = true;
      setSubmitDisabled(form, true);

      var elapsed = Date.now() - renderedAt;
      var isHoneypot = !!fields.ma_hp_x;
      var isTooFast = elapsed < MIN_SUBMIT_MS;
      var isSpam = isHoneypot || isTooFast;
      var spamReason = isHoneypot ? 'honeypot' : isTooFast ? 'too_fast' : '';

      var normalizedMobile = normalizeIrishMobile(fields.mobile);
      var payload = buildPayload(mode, formType, fields, normalizedMobile, isSpam, spamReason);

      try {
        payload.lead_id = await window.GRTracking.requestId(form, payload);
        payload.consent_ad_user_data = window.GRConsent.state().ad_user_data;
        var receipt = await sendLead(payload);
        if (!receipt || receipt.accepted !== true) throw new Error('unconfirmed_submission');
        await pushGenerateLead(mode, formType, payload, receipt);
        window.GRTracking.clearRequest(form);
        if (mode === 'whatsapp') navigateToWhatsApp(buildWhatsAppUrl(formType, fields, normalizedMobile));
        else showSuccess(root, fields, normalizedMobile, formType);
      } catch (err) {
        submitting = false;
        setSubmitDisabled(form, false);
        var notice = form.querySelector('[data-delivery-error]');
        if (!notice) {
          notice = document.createElement('p');
          notice.setAttribute('data-delivery-error', '');
          notice.setAttribute('role', 'alert');
          form.appendChild(notice);
        }
        notice.textContent = 'We could not confirm your enquiry. Please try again; your details are still here.';
      }

    });
  }

  function getFields(form, formType) {
    var fields = {
      child_name: fieldValue(form, 'child_name'),
      parent_name: fieldValue(form, 'parent_name'),
      mobile: fieldValue(form, 'mobile'),
      email: fieldValue(form, 'email'),
      notes: fieldValue(form, 'notes'),
      ma_hp_x: fieldValue(form, 'ma_hp_x')
    };

    if (formType === 'party') {
      fields.child_age = fieldValue(form, 'child_age');
      fields.party_date = fieldValue(form, 'party_date');
      fields.guests = fieldValue(form, 'guests');
    } else {
      fields.child_age = fieldValue(form, 'child_age');
    }

    return fields;
  }

  function fieldValue(form, name) {
    var el = form.elements[name];
    if (!el) return '';
    return (el.value || '').trim();
  }

  /* ------------------------------------------------------------------ */
  /* Validation                                                          */
  /* ------------------------------------------------------------------ */

  function validate(fields, formType) {
    var errors = {};

    if (formType === 'party') {
      if (!fields.child_name || fields.child_name.length < 2) {
        errors.child_name = "Please enter the birthday child's name.";
      }

      var age = toInteger(fields.child_age);
      if (age === null || age < 5 || age > 12) {
        errors.child_age = 'Please enter an age between 5 and 12.';
      }

      if (!fields.party_date || isPastDate(fields.party_date)) {
        errors.party_date = 'Please choose a valid date, not in the past.';
      } else if (isTooFarAhead(fields.party_date)) {
        errors.party_date = 'Please choose a date within the next 2 years.';
      }

      var guests = toInteger(fields.guests);
      if (guests === null || guests < 1 || guests > 25) {
        errors.guests = 'Please enter a number of guests between 1 and 25.';
      }
    } else {
      if (!fields.child_name || fields.child_name.length < 2) {
        errors.child_name = "Please enter your child's full name.";
      }

      var trialAge = toInteger(fields.child_age);
      if (trialAge === null || trialAge < 4 || trialAge > 17) {
        errors.child_age = "Please choose your child's age.";
      }
    }

    if (!fields.parent_name || fields.parent_name.length < 2) {
      errors.parent_name = 'Please enter your full name.';
    }

    if (!fields.mobile || !normalizeIrishMobile(fields.mobile)) {
      errors.mobile = 'Please enter a valid mobile number';
    }

    if (!fields.email || !isValidEmail(fields.email)) {
      errors.email = 'Please enter a valid email address.';
    }

    return errors;
  }

  function isValidEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }

  /**
   * True when `value` (an HTML date input value, "YYYY-MM-DD") is strictly
   * before today in the visitor's local time. Today itself is allowed.
   */
  function isPastDate(value) {
    if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return true;
    var today = new Date();
    var todayStr =
      today.getFullYear() + '-' + pad2(today.getMonth() + 1) + '-' + pad2(today.getDate());
    return value < todayStr;
  }

  /**
   * True when `value` (an HTML date input value, "YYYY-MM-DD") is more than
   * two years ahead of today in the visitor's local time.
   */
  function isTooFarAhead(value) {
    if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
    var today = new Date();
    var maxDate = new Date(today.getFullYear() + 2, today.getMonth(), today.getDate());
    var maxStr =
      maxDate.getFullYear() + '-' + pad2(maxDate.getMonth() + 1) + '-' + pad2(maxDate.getDate());
    return value > maxStr;
  }

  function pad2(value) {
    return value < 10 ? '0' + value : String(value);
  }

  /**
   * Parses `value` as a base-10 integer, returning null (rather than NaN)
   * for anything that isn't a clean integer (blank, decimals, non-numeric).
   * Used so age/guests validation and the payload/email/WhatsApp text all
   * agree on a single normalised integer instead of the raw input string.
   */
  function toInteger(value) {
    if (value === '' || value === null || typeof value === 'undefined') return null;
    var num = Number(value);
    return Number.isInteger(num) ? num : null;
  }

  function normalizeIrishMobile(raw) {
    if (!raw) return null;
    var digits = raw.replace(/[^\d+]/g, '');

    // Convert any leading 00 (international dialing prefix) to +.
    if (digits.indexOf('00') === 0) {
      digits = '+' + digits.slice(2);
    }

    // Drop a stray 0 right after the Irish country code, e.g. "+353 (0)86..."
    // (parentheses/spaces are already stripped above) or "353086...".
    if (digits.indexOf('+3530') === 0) {
      digits = '+353' + digits.slice(5);
    } else if (digits.charAt(0) !== '+' && digits.indexOf('3530') === 0) {
      digits = '353' + digits.slice(4);
    }

    if (digits.indexOf('+353') === 0) {
      var afterCountry = digits.slice(4);
      return /^8\d{8}$/.test(afterCountry) ? '+353' + afterCountry : null;
    }

    if (digits.charAt(0) !== '+' && digits.indexOf('353') === 0) {
      var afterCountry2 = digits.slice(3);
      return /^8\d{8}$/.test(afterCountry2) ? '+353' + afterCountry2 : null;
    }

    if (digits.indexOf('0') === 0) {
      var afterZero = digits.slice(1);
      return /^8\d{8}$/.test(afterZero) ? '+353' + afterZero : null;
    }

    if (/^8\d{8}$/.test(digits)) {
      return '+353' + digits;
    }

    // Any other international number: 8-15 digits after the +.
    if (digits.charAt(0) === '+' && digits.length >= 9 && digits.length <= 16) {
      return digits;
    }

    return null;
  }

  function splitName(fullName) {
    var trimmed = (fullName || '').trim();
    if (!trimmed) return { first: '', last: '' };
    var parts = trimmed.split(/\s+/);
    return { first: parts.shift() || '', last: parts.join(' ') };
  }

  /* ------------------------------------------------------------------ */
  /* Errors / UI state                                                   */
  /* ------------------------------------------------------------------ */

  function clearErrors(form) {
    var errorEls = form.querySelectorAll('[data-lead-form-error]');
    errorEls.forEach(function (el) {
      el.textContent = '';
      el.hidden = true;
    });
    var invalidEls = form.querySelectorAll('[aria-invalid="true"]');
    invalidEls.forEach(function (el) {
      el.removeAttribute('aria-invalid');
    });
  }

  function showErrors(form, errors) {
    var firstInvalid = null;
    Object.keys(errors).forEach(function (name) {
      var input = form.elements[name];
      var errorEl = form.querySelector('[data-lead-form-error="' + name + '"]');
      if (errorEl) {
        errorEl.textContent = errors[name];
        errorEl.hidden = false;
      }
      if (input) {
        input.setAttribute('aria-invalid', 'true');
        if (!firstInvalid) firstInvalid = input;
      }
    });
    if (firstInvalid) firstInvalid.focus();
  }

  function setSubmitDisabled(form, disabled) {
    var button = form.querySelector('[data-lead-form-submit]');
    if (button) button.disabled = disabled;
  }

  function showSuccess(root, fields, normalizedMobile, formType) {
    var form = root.querySelector('[data-lead-form-el]');
    var success = root.querySelector('[data-lead-form-success]');
    if (!success) return;

    var firstName = splitName(fields.parent_name).first || 'there';
    var nameEl = success.querySelector('[data-lead-form-success-name]');
    if (nameEl) nameEl.textContent = firstName;

    var waLink = success.querySelector('[data-lead-form-success-wa]');
    if (waLink) {
      var genericUrl = buildWhatsAppGenericUrl(formType);
      if (genericUrl) {
        waLink.href = genericUrl;
        waLink.addEventListener('click', function () {
          pushDataLayer({
            event: 'whatsapp_click',
            lead_source: 'post_form',
            lead_type: formType,
            link_location: window.location.pathname
          });
        });
      } else {
        waLink.hidden = true;
      }
    }

    if (form) form.hidden = true;
    success.hidden = false;
    success.focus();
  }

  /* ------------------------------------------------------------------ */
  /* Payload / submission                                               */
  /* ------------------------------------------------------------------ */

  function buildPayload(mode, formType, fields, normalizedMobile, isSpam, spamReason) {
    var attribution = readAttribution() || {};
    var payload = {
      source: mode === 'whatsapp' ? 'whatsapp' : 'form',
      form_type: formType,
      page_url: window.location.origin + window.location.pathname,
      page_path: window.location.pathname,
      child_name: fields.child_name,
      parent_name: fields.parent_name,
      mobile: normalizedMobile || fields.mobile,
      email: fields.email,
      notes: fields.notes,
      landing_page: attribution.landing_page || '',
      submitted_at: new Date().toISOString(),
      spam: !!isSpam,
      reason: isSpam ? spamReason || '' : ''
    };

    payload.child_age = fields.child_age;
    if (formType === 'party') {
      payload.party_date = fields.party_date;
      payload.guests = fields.guests;
    }

    TRACKED_KEYS.forEach(function (key) {
      payload[key] = attribution[key] || '';
    });

    return payload;
  }

  async function sendLead(payload) {
    if (!config.endpoint) throw new Error('missing_endpoint');
    var response = await fetch(config.endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain;charset=utf-8' },
      body: JSON.stringify(payload)
    });
    if (!response.ok) throw new Error('delivery_failed');
    return response.json();
  }

  function pushGenerateLead(mode, formType, payload, receipt) {
    return window.GRTracking.emitLead(receipt, {
      lead_source: mode === 'whatsapp' ? 'whatsapp' : 'form',
      lead_type: formType,
      form_location: payload.page_path,
      value: formType === 'party' ? config.partyLeadValue : config.leadValue,
      currency: 'EUR'
    }, { email: payload.email, phone_number: payload.mobile });
  }

  function pushDataLayer(data) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(data);
  }

  /* ------------------------------------------------------------------ */
  /* WhatsApp                                                            */
  /* ------------------------------------------------------------------ */

  /**
   * Formats an HTML date input value ("YYYY-MM-DD") as dd/mm/yyyy. Used for
   * the preferred party date (party forms).
   */
  function formatIsoDate(value) {
    if (!value) return '';
    var parts = value.split('-');
    if (parts.length !== 3) return value;
    return parts[2] + '/' + parts[1] + '/' + parts[0];
  }

  function whatsappDigits() {
    return (config.whatsappNumber || '').replace(/\D/g, '');
  }

  function buildWhatsAppUrl(formType, fields, normalizedMobile) {
    var digits = whatsappDigits();
    var lines;

    if (formType === 'party') {
      lines = [
        "Hi Miss Ali! I'd like to enquire about a birthday party.",
        'Birthday child: ' + fields.child_name + ' (turning ' + fields.child_age + ')',
        'Preferred date: ' + formatIsoDate(fields.party_date),
        'Guests: ' + fields.guests,
        'Parent: ' + fields.parent_name,
        'Mobile: ' + (normalizedMobile || fields.mobile),
        'Email: ' + fields.email
      ];
    } else {
      lines = [
        "Hi Miss Ali! I'd like to book a Meet & Greet trial class.",
        'Child: ' + fields.child_name + ' (age ' + fields.child_age + ')',
        'Parent: ' + fields.parent_name,
        'Mobile: ' + (normalizedMobile || fields.mobile),
        'Email: ' + fields.email
      ];
    }

    if (fields.notes) {
      lines.push('Notes: ' + fields.notes);
    }
    var text = encodeURIComponent(lines.join('\n'));
    return digits ? 'https://wa.me/' + digits + '?text=' + text : '';
  }

  /**
   * A PII-free prefilled message for the post-submission "Message us on
   * WhatsApp now" link, so nothing from the form fields ends up in a URL
   * that could be shared, logged or screenshotted.
   */
  function buildWhatsAppGenericUrl(formType) {
    var digits = whatsappDigits();
    if (!digits) return '';
    var message =
      formType === 'party'
        ? "Hi Miss Ali, I've just sent a party enquiry on your website."
        : "Hi Miss Ali, I've just requested a Meet & Greet on your website.";
    var text = encodeURIComponent(message);
    return 'https://wa.me/' + digits + '?text=' + text;
  }

  function navigateToWhatsApp(waUrl) {
    if (!waUrl) return;
    window.location.href = waUrl;
  }

  /**
   * Site-wide, PII-free click tracking for any wa.me / WhatsApp Business
   * API link on the page (e.g. links written into page copy). Runs
   * regardless of whether the WhatsApp gate (snippets/whatsapp-gate.liquid)
   * is enabled, since those links exist independently of the gate.
   *
   * Excludes:
   * - [data-whatsapp-direct] links (the post-submission success link),
   *   which push their own whatsapp_click event with more context.
   * - [data-whatsapp-gate] links, which the gate intercepts to open the
   *   modal instead of navigating straight to WhatsApp.
   */
  function initWhatsAppClickTracking() {
    document.addEventListener('click', function (event) {
      var waLink = event.target.closest('a[href*="wa.me"], a[href*="api.whatsapp.com"]');
      if (!waLink) return;
      if (waLink.hasAttribute('data-whatsapp-direct') || waLink.closest('[data-whatsapp-direct]')) {
        return;
      }
      if (waLink.hasAttribute('data-whatsapp-gate') || waLink.closest('[data-whatsapp-gate]')) {
        return;
      }

      pushDataLayer({ event: 'whatsapp_click', link_location: window.location.pathname });
    });
  }
})();
